import { useState, type FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Phone, Trash2, Plus, Save, CheckCircle2 } from 'lucide-react'
import { api, messaggioErrore } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Badge } from '../../components/ui/Badge'
import { EmptyState } from '../../components/ui/EmptyState'
import { Modal } from '../../components/ui/Modal'
import { etichettaPiano, variantePiano } from '../../lib/format'
import { useAuthStore } from '../../stores/authStore'
import { useUpgrade } from '../../hooks/useUpgrade'
import type { Utente } from '../../lib/tipi'
import pagina from '../pagine.module.css'

interface Telefoni {
  principale: { telefono: string; etichetta: string }
  aggiuntivi: Array<{ id: string; telefono: string; etichetta: string }>
  limite: number | null
  disponibili: number | null
}

export function SdrProfilo() {
  const queryClient = useQueryClient()
  const aggiornaUtente = useAuthStore((s) => s.aggiornaUtente)
  const { avviaUpgrade, caricamento: caricamentoUpgrade, errore: erroreUpgrade } = useUpgrade()

  const [nome, setNome] = useState('')
  const [cognome, setCognome] = useState('')
  const [azienda, setAzienda] = useState('')
  const [salvato, setSalvato] = useState(false)
  const [erroreSalva, setErroreSalva] = useState<string | null>(null)
  const [modaleAggiungi, setModaleAggiungi] = useState(false)

  const profilo = useQuery({
    queryKey: ['profilo'],
    queryFn: async () => {
      const dati = (await api.get<{ dati: Utente }>('/auth/profilo')).data.dati
      if (dati) {
        setNome(dati.nome)
        setCognome(dati.cognome)
        setAzienda(dati.azienda ?? '')
        aggiornaUtente(dati)
      }
      return dati
    },
  })

  const telefoni = useQuery({
    queryKey: ['telefoni'],
    queryFn: async () => (await api.get<{ dati: Telefoni }>('/auth/telefoni')).data.dati,
  })

  const salva = useMutation({
    mutationFn: async () => api.put('/auth/profilo', { nome, cognome, azienda }),
    onSuccess: () => {
      setSalvato(true)
      setErroreSalva(null)
      queryClient.invalidateQueries({ queryKey: ['profilo'] })
      setTimeout(() => setSalvato(false), 3000)
    },
    onError: (err) => setErroreSalva(messaggioErrore(err)),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    salva.mutate()
  }

  if (profilo.isLoading) return <div className={pagina.skeleton}>Caricamento…</div>
  if (!profilo.data) return <EmptyState titolo="Errore caricamento profilo" />

  const u = profilo.data

  return (
    <div className={pagina.wrap}>
      <div className={pagina.header}>
        <div>
          <h1 className={pagina.titolo}>Il tuo profilo</h1>
          <div className={pagina.sottotitolo}>Aggiorna i tuoi dati e gestisci i numeri abilitati.</div>
        </div>
      </div>

      <div className={pagina.due}>
        <Card titolo="Dati personali">
          <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Riga etichetta="Email" valore={u.email} disabilitato />
            <CampoTesto label="Nome" valore={nome} onChange={setNome} />
            <CampoTesto label="Cognome" valore={cognome} onChange={setCognome} />
            <CampoTesto label="Azienda" valore={azienda} onChange={setAzienda} />

            {salvato && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 12px', background: 'var(--success-soft)', color: 'var(--success)',
                borderRadius: 'var(--radius-md)', fontSize: 13,
              }}>
                <CheckCircle2 size={14} /> Profilo aggiornato
              </div>
            )}
            {erroreSalva && (
              <div style={{
                padding: '8px 12px', background: 'var(--danger-soft)', color: 'var(--danger)',
                borderRadius: 'var(--radius-md)', fontSize: 13,
              }}>
                {erroreSalva}
              </div>
            )}

            <Button type="submit" iconaSinistra={<Save size={14} />} caricamento={salva.isPending}>
              Salva modifiche
            </Button>
          </form>
        </Card>

        <Card titolo="Piano">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <Badge variante={variantePiano(u.piano)}>{etichettaPiano(u.piano)}</Badge>
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              Sessioni totali: <strong className="mono">{u.sessioniTotali}</strong><br />
              Account creato: {new Date(u.createdAt).toLocaleDateString('it-IT')}<br />
              {u.lastLogin && <>Ultimo accesso: {new Date(u.lastLogin).toLocaleDateString('it-IT')}</>}
            </div>
            {u.piano === 'free' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Button
                  variante="primary"
                  taglia="sm"
                  caricamento={caricamentoUpgrade === 'pro'}
                  disabled={caricamentoUpgrade !== null}
                  onClick={() => avviaUpgrade('pro')}
                >
                  {caricamentoUpgrade === 'pro' ? 'Reindirizzamento…' : 'Passa a Pro — 7 giorni gratis'}
                </Button>
                {erroreUpgrade && (
                  <div style={{ fontSize: 12, color: 'var(--danger)' }}>{erroreUpgrade}</div>
                )}
              </div>
            )}
          </div>
        </Card>
      </div>

      <Card
        titolo="Numeri di telefono abilitati"
        azione={
          (u.piano === 'pro' && telefoni.data && (telefoni.data.disponibili ?? 0) > 0) ? (
            <Button taglia="sm" iconaSinistra={<Plus size={14} />} onClick={() => setModaleAggiungi(true)}>
              Aggiungi numero
            </Button>
          ) : undefined
        }
      >
        {telefoni.data ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <RigaTelefono
              telefono={telefoni.data.principale.telefono}
              etichetta="Principale"
              principale
            />
            {telefoni.data.aggiuntivi.map((t) => (
              <RigaTelefono
                key={t.id}
                telefono={t.telefono}
                etichetta={t.etichetta}
                onElimina={async () => {
                  if (!confirm(`Eliminare il numero ${t.telefono}?`)) return
                  await api.delete(`/auth/telefoni/${t.id}`)
                  queryClient.invalidateQueries({ queryKey: ['telefoni'] })
                }}
              />
            ))}
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
              {u.piano === 'free' && 'Piano Free: solo numero principale. Passa a Pro per aggiungere 1 numero in più.'}
              {u.piano === 'pro' && `Piano Pro: max 2 numeri (${telefoni.data.aggiuntivi.length + 1}/2 in uso).`}
              {(u.piano === 'team' || u.piano === 'enterprise') && 'Piano Team/Enterprise: numeri illimitati gestiti dal manager.'}
            </div>
          </div>
        ) : (
          <EmptyState titolo="Caricamento…" />
        )}
      </Card>

      <ModaleAggiungiNumero
        aperto={modaleAggiungi}
        onChiudi={() => setModaleAggiungi(false)}
        onAggiunto={() => {
          setModaleAggiungi(false)
          queryClient.invalidateQueries({ queryKey: ['telefoni'] })
        }}
      />
    </div>
  )
}

function Riga({ etichetta, valore, disabilitato }: { etichetta: string; valore: string; disabilitato?: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{etichetta}</label>
      <div style={{
        height: 40, padding: '0 12px', display: 'flex', alignItems: 'center',
        background: disabilitato ? 'var(--bg-subtle)' : 'var(--surface)',
        border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
        color: disabilitato ? 'var(--text-muted)' : 'var(--text-primary)',
        fontSize: 14,
      }}>
        {valore}
      </div>
    </div>
  )
}

function CampoTesto({ label, valore, onChange }: { label: string; valore: string; onChange: (v: string) => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{label}</label>
      <input
        type="text"
        value={valore}
        onChange={(e) => onChange(e.target.value)}
        className={pagina.input}
        style={{ height: 40 }}
      />
    </div>
  )
}

function RigaTelefono({
  telefono, etichetta, principale, onElimina,
}: { telefono: string; etichetta: string; principale?: boolean; onElimina?: () => void }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '12px 14px', background: 'var(--bg-subtle)',
      borderRadius: 'var(--radius-md)',
    }}>
      <Phone size={18} color="var(--text-secondary)" />
      <div style={{ flex: 1 }}>
        <div className="mono" style={{ fontWeight: 600 }}>{telefono}</div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{etichetta}</div>
      </div>
      {principale ? (
        <Badge variante="accent">Principale</Badge>
      ) : (
        onElimina && (
          <Button variante="ghost" taglia="sm" onClick={onElimina}>
            <Trash2 size={14} color="var(--danger)" />
          </Button>
        )
      )}
    </div>
  )
}

function ModaleAggiungiNumero({ aperto, onChiudi, onAggiunto }:
  { aperto: boolean; onChiudi: () => void; onAggiunto: () => void }) {
  const [tel, setTel] = useState('')
  const [etichetta, setEtichetta] = useState('Cellulare')
  const [errore, setErrore] = useState<string | null>(null)
  const [caricamento, setCaricamento] = useState(false)

  const aggiungi = async (e: FormEvent) => {
    e.preventDefault()
    setErrore(null)
    setCaricamento(true)
    try {
      await api.post('/auth/telefoni', { telefono: tel, etichetta })
      setTel(''); setEtichetta('Cellulare')
      onAggiunto()
    } catch (err) {
      setErrore(messaggioErrore(err))
    } finally {
      setCaricamento(false)
    }
  }

  return (
    <Modal aperto={aperto} titolo="Aggiungi un numero" onChiudi={onChiudi}>
      <form onSubmit={aggiungi} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <CampoTesto label="Numero (E.164)" valore={tel} onChange={setTel} />
        <CampoTesto label="Etichetta" valore={etichetta} onChange={setEtichetta} />
        {errore && (
          <div style={{ padding: 10, background: 'var(--danger-soft)', color: 'var(--danger)', borderRadius: 'var(--radius-md)', fontSize: 13 }}>
            {errore}
          </div>
        )}
        <Button type="submit" caricamento={caricamento}>Aggiungi</Button>
      </form>
    </Modal>
  )
}
